import React, { useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import loadable from '@loadable/component';

import * as pageDataLoadingAPI from './containers/pageDataLoadingAPI';
import { NamedRedirect, InquiryWizardProvider } from './components';
import { BLACKLISTED_TRANSACTION_IDS } from './blacklist';

const AgritourismStatutesPage = loadable(() =>
  import(
    /* webpackChunkName: "AgritourismStatutesPage" */ './containers/AgritourismStatutesPage/AgritourismStatutesPage'
  )
);
const AuthenticationPage = loadable(() =>
  import(
    /* webpackChunkName: "AuthenticationPage" */ './containers/AuthenticationPage/AuthenticationPage'
  )
);
const CheckoutPage = loadable(() =>
  import(/* webpackChunkName: "CheckoutPage" */ './containers/CheckoutPage/CheckoutPage')
);
const EditListingPage = loadable(() =>
  import(/* webpackChunkName: "EditListingPage" */ './containers/EditListingPage/EditListingPage')
);
const EmailVerificationPage = loadable(() =>
  import(
    /* webpackChunkName: "EmailVerificationPage" */ './containers/EmailVerificationPage/EmailVerificationPage'
  )
);
const FAQLandownersInfoPage = loadable(() =>
  import(
    /* webpackChunkName: "FAQLandownersInfoPage" */ './containers/FAQLandownersInfoPage/FAQLandownersInfoPage'
  )
);
const IDVerificationPage = loadable(() =>
  import(
    /* webpackChunkName: "IDVerificationPage" */ './containers/IDVerificationPage/IDVerificationPage'
  )
);
const InboxPageV2 = loadable(() =>
  import(/* webpackChunkName: "InboxPageV2" */ './containers/InboxPageV2/InboxPageV2')
);
const LandingPage = loadable(() =>
  import(/* webpackChunkName: "LandingPage" */ './containers/LandingPage/LandingPage')
);
const LandownerReferralPage = loadable(() =>
  import(
    /* webpackChunkName: "LandownerReferralPage" */ './containers/LandownerReferralPage/LandownerReferralPage'
  )
);
const ListingPage = loadable(() =>
  import(/* webpackChunkName: "ListingPage" */ './containers/ListingPage/ListingPage')
);
const ManageListingsPage = loadable(() =>
  import(
    /* webpackChunkName: "ManageListingsPage" */ './containers/ManageListingsPage/ManageListingsPage'
  )
);
const MembershipSignupPage = loadable(() =>
  import(
    /* webpackChunkName: "MembershipSignupPage" */ './containers/MembershipSignupPage/MembershipSignupPage'
  )
);
const MobileTermsOfServicePage = loadable(() =>
  import(
    /* webpackChunkName: "MobileTermsOfServicePage" */ './containers/MobileTermsOfServicePage/MobileTermsOfServicePage'
  )
);
const NewInquiryPage = loadable(() =>
  import(/* webpackChunkName: "NewInquiryPage" */ './containers/NewInquiryPage/NewInquiryPage')
);
const NotFoundPage = loadable(() =>
  import(/* webpackChunkName: "NotFoundPage" */ './containers/NotFoundPage/NotFoundPage')
);
const PackagePage = loadable(() =>
  import(/* webpackChunkName: "PackagePage" */ './containers/PackagePage/PackagePage')
);
const PasswordRecoveryPage = loadable(() =>
  import(
    /* webpackChunkName: "PasswordRecoveryPage" */ './containers/PasswordRecoveryPage/PasswordRecoveryPage'
  )
);
const PasswordResetPage = loadable(() =>
  import(
    /* webpackChunkName: "PasswordResetPage" */ './containers/PasswordResetPage/PasswordResetPage'
  )
);
const PricingPage = loadable(() =>
  import(/* webpackChunkName: "PricingPage" */ './containers/PricingPage/PricingPage')
);
const PrivacyPolicyPage = loadable(() =>
  import(
    /* webpackChunkName: "PrivacyPolicyPage" */ './containers/PrivacyPolicyPage/PrivacyPolicyPage'
  )
);
const ProfilePage = loadable(() =>
  import(/* webpackChunkName: "ProfilePage" */ './containers/ProfilePage/ProfilePage')
);
const ProfileSettingsPage = loadable(() =>
  import(
    /* webpackChunkName: "ProfileSettingsPage" */ './containers/ProfileSettingsPage/ProfileSettingsPage'
  )
);
const ReviewPage = loadable(() =>
  import(/* webpackChunkName: "ReviewPage" */ './containers/ReviewPage/ReviewPage')
);
const SearchPage = loadable(() =>
  import(/* webpackChunkName: "SearchPage" */ './containers/SearchPage/SearchPage')
);
const StyleguidePage = loadable(() =>
  import(/* webpackChunkName: "StyleguidePage" */ './containers/StyleguidePage/StyleguidePage')
);
const TermsOfServicePage = loadable(() =>
  import(
    /* webpackChunkName: "TermsOfServicePage" */ './containers/TermsOfServicePage/TermsOfServicePage'
  )
);

// https://en.wikipedia.org/wiki/Universally_unique_identifier#Nil_UUID
const draftId = '00000000-0000-0000-0000-000000000000';
const draftSlug = 'draft';

// NOTE: this could get into an infinite reload loop, if not run
// under proxy
const NewUI = () => {
  useEffect(() => {
    window.location.reload();
  }, []);

  return null;
};

const RedirectToLandingPage = () => {
  const { affiliate } = useParams();
  const location = useLocation();
  let referralMethodFromURL = '';

  if (affiliate) {
    referralMethodFromURL = 'affiliate';
  } else {
    referralMethodFromURL = new URLSearchParams(location.search).get('referralMethod') || '';
  }

  return (
    <NamedRedirect
      name="LandingPage"
      search={referralMethodFromURL ? `?referralMethod=${referralMethodFromURL}` : ''}
    />
  );
};

// Our routes are exact by default.
// See behaviour from Routes.js where Route is created.
const routeConfiguration = () => {
  return [
    {
      path: '/',
      name: 'LandingPage',
      component: props =>
        // To avoid infinite redirect loop in dev we still want to load the old version
        process.env.REACT_APP_IN_PREVIEW === 'true' ? <LandingPage {...props} /> : <NewUI />,
      loadData: pageDataLoadingAPI.LandingPage.loadData,
    },
    {
      path: '/faq/landowners',
      name: 'FAQLandownersPage',
      component: () => <NamedRedirect name="LandownersPage" />,
    },
    {
      path: '/landowners',
      name: 'LandownersPage',
      component: () => <NewUI />,
    },
    {
      path: '/dashboard',
      name: 'DashboardPage',
      component: () => <NewUI />,
    },
    {
      path: '/onland',
      name: 'OnlandPage',
      component: () => <NewUI />,
    },
    {
      path: '/faq/landowners-info',
      name: 'FAQLandownersInfoPage',
      component: FAQLandownersInfoPage,
    },
    {
      path: '/about',
      name: 'AboutPage',
      component: () => <NewUI />,
    },
    {
      path: '/partners/conservation',
      name: 'ConservationPage',
      component: () => <NewUI />,
    },
    {
      path: '/contact',
      name: 'ContactPage',
      component: () => <NewUI />,
    },
    {
      path: '/partners/conservation',
      name: 'ConservationPartners',
      component: () => <NewUI />,
    },
    {
      path: '/insurance',
      name: 'InsurancePage',
      component: () => <NewUI />,
    },
    {
      path: '/perks',
      name: 'PerksPage',
      component: () => <NewUI />,
    },
    {
      path: '/media',
      name: 'MediaPage',
      component: () => <NewUI />,
    },
    {
      path: '/u/:id/favorites/:tab',
      name: 'FavoritesPage',
      component: () => <NewUI />,
    },
    {
      path: '/states/:slug',
      name: 'StatesPage',
      component: () => <NewUI />,
    },
    {
      path: '/t/:slug',
      name: 'BookingOrTripsPage',
      component: () => <NewUI />,
    },
    {
      path: '/boa',
      name: 'BusinessOfAgriculture',
      component: function BusinessOfAgriculture() {
        useEffect(() => {
          window.location.href = 'https://landowners.landtrust.com/business-of-agriculture';
        }, []);

        return null;
      },
    },
    {
      path: '/ranchinvestor',
      name: 'RanchInvestor',
      component: function RanchInvestor() {
        useEffect(() => {
          window.location.href = 'https://landowners.landtrust.com/ranchinvestor';
        }, []);

        return null;
      },
    },
    {
      path: '/review/:transactionRole/:id',
      name: 'ReviewPage',
      auth: true,
      authPage: 'LoginPage',
      component: ReviewPage,
    },
    {
      path: '/v2/review/:transactionRole/:id',
      name: 'ReviewPageV2',
      component: () => <NewUI />,
    },
    {
      path: '/s',
      name: 'SearchPage',
      component: props =>
        process.env.REACT_APP_IN_PREVIEW === 'true' ? <SearchPage {...props} /> : <NewUI />,
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/filters',
      name: 'SearchFiltersPage',
      component: SearchPage,
      extraProps: { tab: 'filters' },
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/listings',
      name: 'SearchListingsPage',
      component: SearchPage,
      extraProps: { tab: 'listings' },
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/s/map',
      name: 'SearchMapPage',
      component: SearchPage,
      extraProps: { tab: 'map' },
      loadData: pageDataLoadingAPI.SearchPage.loadData,
    },
    {
      path: '/l',
      name: 'ListingBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/l/:slug/:id',
      name: 'ListingPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/package/:packageId',
      name: 'PackagePage',
      component: PackagePage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/inquiry/:step',
      name: 'NewInquiryPage',
      component: props => {
        const { params, history, location } = props;
        return (
          <InquiryWizardProvider defaultValues={{ params, history, location }}>
            <NewInquiryPage {...props} />
          </InquiryWizardProvider>
        );
      },
      loadData: pageDataLoadingAPI.NewInquiryPage.loadData,
    },
    {
      path: '/l/:slug/:id/a/:activity/:variant',
      name: 'ActivityPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        return <NamedRedirect name="ListingPageVariant" params={{ ...params }} />;
      },
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/a/:activity',
      name: 'ActivityPage',
      component: props => {
        const { params } = props;

        return <NamedRedirect name="ListingPage" params={{ ...params }} />;
      },
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/package/:packageId/pricing',
      name: 'PricingPackagePage',
      component: PricingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/package/:packageId/checkout',
      name: 'CheckoutPackagePage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/package/:packageId/:variant',
      name: 'PackagePageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: PackagePage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/checkout',
      name: 'CheckoutPage',
      auth: true,
      component: CheckoutPage,
      setInitialValues: pageDataLoadingAPI.CheckoutPage.setInitialValues,
    },
    {
      path: '/l/:slug/:id/:variant',
      name: 'ListingPageVariant',
      auth: true,
      authPage: 'LoginPage',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/l/new',
      name: 'NewListingPage',
      auth: true,
      component: () => (
        <NamedRedirect
          name="EditListingPage"
          params={{ slug: draftSlug, id: draftId, type: 'new', tab: 'location' }}
        />
      ),
    },
    {
      path: '/l/:slug/:id/:type/:tab',
      name: 'EditListingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/p/:packageId',
      name: 'EditListingPagePackage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },
    {
      path: '/l/:slug/:id/:type/:tab/:returnURLType',
      name: 'EditListingStripeOnboardingPage',
      auth: true,
      component: EditListingPage,
      loadData: pageDataLoadingAPI.EditListingPage.loadData,
    },

    // Canonical path should be after the `/l/new` path since they
    // conflict and `new` is not a valid listing UUID.
    {
      path: '/l/:id',
      name: 'ListingPageCanonical',
      component: ListingPage,
      loadData: pageDataLoadingAPI.ListingPage.loadData,
    },
    {
      path: '/u',
      name: 'ProfileBasePage',
      component: RedirectToLandingPage,
    },
    {
      path: '/u/:id',
      name: 'ProfilePage',
      component: ProfilePage,
      loadData: pageDataLoadingAPI.ProfilePage.loadData,
    },
    {
      path: '/profile-settings',
      name: 'ProfileSettingsBasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => (
        <NamedRedirect name="ProfileSettingsPage" params={{ tab: 'account-details' }} />
      ),
    },
    {
      path: '/profile-settings/payout-details/:returnURLType',
      name: 'ProfileSettingsPayoutOnboardingPage',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        const newProps = {
          ...props,
          params: {
            ...params,
            tab: 'payout-details',
          },
        };
        return <ProfileSettingsPage {...newProps} />;
      },
    },
    {
      path: '/profile-settings/id-verification',
      name: 'IDVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: IDVerificationPage,
    },
    {
      path: '/profile-settings/:tab',
      name: 'ProfileSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ProfileSettingsPage,
    },

    // Note: authenticating with IdP (e.g. Facebook) expects that /login path exists
    // so that in the error case users can be redirected back to the LoginPage
    // In case you change this, remember to update the route in server/api/auth/loginWithIdp.js
    {
      path: '/login',
      name: 'LoginPage',
      component: AuthenticationPage,
      extraProps: { tab: 'login' },
    },
    {
      path: '/signup',
      name: 'SignupPage',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/plus/membership/signup',
      name: 'MembershipSignupPage',
      component: MembershipSignupPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/a/:affiliate?',
      name: 'Affiliate',
      component: AuthenticationPage,
      extraProps: { tab: 'signup' },
    },
    {
      path: '/confirm',
      name: 'ConfirmPage',
      component: AuthenticationPage,
      extraProps: { tab: 'confirm' },
    },
    {
      path: '/recover-password',
      name: 'PasswordRecoveryPage',
      component: PasswordRecoveryPage,
    },
    {
      path: '/activity',
      name: 'RecentActivityPage',
      component: () => <NewUI />,
    },
    {
      path: '/huntprivate',
      name: 'HuntPrivatePage',
      component: () => <NewUI />,
    },
    {
      path: '/plus',
      name: 'LTPlusPage',
      component: () => <NewUI />,
    },
    {
      path: '/plus/membership#discount',
      name: 'LTPlusCheckoutPageDiscount',
      component: () => <NewUI />,
    },
    {
      path: '/plus/membership',
      name: 'LTPlusCheckoutPage',
      component: () => <NewUI />,
    },
    {
      path: '/fieldnotes',
      name: 'FieldNotesPage',
      component: () => <NewUI />,
    },

    {
      path: '/inbox',
      name: 'InboxV2BasePage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="InboxV2Page" params={{ tab: 'orders' }} />,
    },
    {
      path: '/inbox/:tab/:id?',
      name: 'InboxV2Page',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props || {};

        if (params?.id && BLACKLISTED_TRANSACTION_IDS.includes(params.id)) {
          return <NamedRedirect name="InboxV2BasePage" />;
        }

        const newParams = {
          ...params,
        };

        if (params?.tab === 'sales') {
          newParams.transactionRole = 'provider';
        } else {
          newParams.transactionRole = 'customer';
        }

        return <InboxPageV2 {...props} params={newParams} />;
      },
    },
    {
      path: '/order/:id',
      name: 'OrderPage',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        return <NamedRedirect name="OrderDetailsPage" params={{ ...params }} />;
      },
    },
    {
      path: '/order/:id/details',
      name: 'OrderDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        return (
          <NamedRedirect
            name="InboxV2Page"
            params={{ ...params, tab: 'orders', transactionRole: 'customer' }}
          />
        );
      },
      loadData: params =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'customer' }),
      setInitialValues: pageDataLoadingAPI.TransactionPage.setInitialValues,
    },
    {
      path: '/sale/:id',
      name: 'SalePage',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        return <NamedRedirect name="SaleDetailsPage" params={{ ...params }} />;
      },
    },
    {
      path: '/sale/:id/details',
      name: 'SaleDetailsPage',
      auth: true,
      authPage: 'LoginPage',
      component: props => {
        const { params } = props;

        return (
          <NamedRedirect
            name="InboxV2Page"
            params={{ ...params, tab: 'sales', transactionRole: 'provider' }}
          />
        );
      },
      loadData: params =>
        pageDataLoadingAPI.TransactionPage.loadData({ ...params, transactionRole: 'provider' }),
    },
    {
      path: '/listings',
      name: 'ManageListingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: ManageListingsPage,
      loadData: pageDataLoadingAPI.ManageListingsPage.loadData,
    },
    {
      path: '/account',
      name: 'AccountSettingsPage',
      auth: true,
      authPage: 'LoginPage',
      component: () => <NamedRedirect name="ProfileSettingsBasePage" />,
    },

    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage,
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage,
    },
    {
      path: '/landowner-referral',
      name: 'LandownerReferralPage',
      component: LandownerReferralPage,
    },
    {
      path: '/agritourism-statutes',
      name: 'AgritourismStatutesPage',
      component: AgritourismStatutesPage,
    },
    {
      path: '/mobile-terms-of-service',
      name: 'MobileTermsOfServicePage',
      component: MobileTermsOfServicePage,
    },
    {
      path: '/styleguide',
      name: 'Styleguide',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/g/:group',
      name: 'StyleguideGroup',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component',
      name: 'StyleguideComponent',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example',
      name: 'StyleguideComponentExample',
      component: StyleguidePage,
    },
    {
      path: '/styleguide/c/:component/:example/raw',
      name: 'StyleguideComponentExampleRaw',
      component: StyleguidePage,
      extraProps: { raw: true },
    },
    {
      path: '/notfound',
      name: 'NotFoundPage',
      component: NotFoundPage,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /reset-password endpoint
    {
      path: '/reset-password',
      name: 'PasswordResetPage',
      component: PasswordResetPage,
    },

    // Do not change this path!
    //
    // The API expects that the application implements /verify-email endpoint
    {
      path: '/verify-email',
      name: 'EmailVerificationPage',
      auth: true,
      authPage: 'LoginPage',
      component: EmailVerificationPage,
      loadData: pageDataLoadingAPI.EmailVerificationPage.loadData,
    },
  ];
};

export default routeConfiguration;
